import React from "react";
import { fetchWithTimeout, getApiBackoffUntil, setApiBackoff } from "../sync/apiClient";
import {
  clearStoredActiveFarm,
  clearStoredSession,
  clearUpdateDismissalState,
  readStoredActiveFarm,
  readStoredSession,
  writeStoredActiveFarm,
  writeStoredSession,
} from "../lib/sessionUtils";

export function useStoredSessionState() {
  return React.useState(() => readStoredSession());
}

export function useStoredActiveFarmState() {
  return React.useState(() => readStoredActiveFarm());
}

export function useMaintenanceInfo({ API_URL, API_SECRET, DB, getDeviceId }) {
  const [maintInfo, setMaintInfo] = React.useState(() => {
    try {
      return JSON.parse(localStorage.getItem("fz:maintenance") || "null");
    } catch (e) {
      return null;
    }
  });

  const maintenanceInFlightRef = React.useRef(false);
  const maintenanceBackoffUntilRef = React.useRef(0);

  React.useEffect(() => {
    let stopped = false;

    async function checkMaint() {
      const now = Date.now();
      if (maintenanceInFlightRef.current) return;
      if (maintenanceBackoffUntilRef.current > now) return;
      const apiBackoffUntil = typeof getApiBackoffUntil === "function" ? getApiBackoffUntil() : 0;
      if (apiBackoffUntil > now) {
        maintenanceBackoffUntilRef.current = apiBackoffUntil;
        return;
      }

      maintenanceInFlightRef.current = true;
      try {
        const local = await DB.get("fz:maintenance");
        if (stopped) return;
        if (local !== undefined) {
          setMaintInfo(local);
          localStorage.setItem("fz:maintenance", local ? JSON.stringify(local) : "null");
        }
        // v3.9.13: manutenção não compete com bundle durante snapshot.
        // Respeita farmId — não bloquear se não há farm ativa ou farm diferente.
        try {
          const _ex = typeof window !== "undefined" ? window.__fzBootstrapExclusiveMode : null;
          if (_ex?.active && _ex?.phase === "snapshot" && _ex?.farmId) return;
        } catch (_) {}
        const res = await fetchWithTimeout(API_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": API_SECRET,
            "X-Device-ID": getDeviceId(),
          },
          body: JSON.stringify({ action: "pull_global", key: "fz:maintenance" }),
        }, 8000);
        if (res?.status === 429 || res?.status === 503) {
          const delay = res.status === 429 ? 180000 : 120000;
          try { setApiBackoff(delay); } catch (_) {}
          maintenanceBackoffUntilRef.current = Date.now() + delay;
          return;
        }
        const json = await res.json();
        if (stopped) return;
        if (json?.success && json.data?.data !== undefined) {
          const srvMaint = json.data.data;
          setMaintInfo(srvMaint);
          await DB.set("fz:maintenance", srvMaint);
          localStorage.setItem("fz:maintenance", srvMaint ? JSON.stringify(srvMaint) : "null");
        }
      } catch (e) {
        if (e?.status === 429 || e?.status === 503 || e?.message === "api-rate-limited-backoff") {
          maintenanceBackoffUntilRef.current = e?.backoffUntil || (Date.now() + 120000);
        }
      } finally {
        maintenanceInFlightRef.current = false;
      }
    }

    // v3.2.07: delay inicial maior para não competir com globais secundários do pré-login.
    // setInterval de 120s mantido — apenas o primeiro disparo é atrasado.
    const initialTimer = setTimeout(checkMaint, 10000);
    const timer = setInterval(checkMaint, 120000);
    return () => {
      stopped = true;
      clearTimeout(initialTimer);
      clearInterval(timer);
    };
  }, [API_SECRET, API_URL, DB, getDeviceId]);

  const exitMaintenance = React.useCallback(() => {
    localStorage.removeItem("fz:maintenance");
    DB.set("fz:maintenance", null);
    setMaintInfo(null);
  }, [DB]);

  return { maintInfo, exitMaintenance };
}

export function createHandleLogin({
  setSession,
  setActiveFarm,
  setBootTelemetryIdentity,
  markBootTelemetry,
  normalizeUiPreferences,
  resetMobileViewportAfterLogin,
}) {
  return function handleLogin(sess, farm) {
    resetMobileViewportAfterLogin();
    const normalizedSession = { ...sess, uiPreferences: normalizeUiPreferences(sess?.uiPreferences) };
    setBootTelemetryIdentity(normalizedSession, farm);
    markBootTelemetry("user-login", { role: normalizedSession?.role || "", farmId: farm?.id || "" });
    setSession(normalizedSession);
    writeStoredSession(normalizedSession);
    if (normalizedSession.role === "solicitante") {
      setActiveFarm(farm);
      writeStoredActiveFarm(farm);
    } else {
      setActiveFarm(null);
      clearStoredActiveFarm();
    }
  };
}

export function createHandleLogout({ setSession, setActiveFarm, vcheck }) {
  return function handleLogout() {
    const ok = typeof window === "undefined" ? true : window.confirm("Deseja realmente sair do app?");
    if (!ok) return;
    clearUpdateDismissalState();
    setSession(null);
    setActiveFarm(null);
    clearStoredSession();
    clearStoredActiveFarm();
    setTimeout(() => {
      try {
        vcheck?.checkForUpdates?.({ keepVisible: true, ignoreDismissed: true, forceVisible: true });
      } catch (e) {}
    }, 80);
  };
}

export function useSolicitanteFarmGuard({ session, activeFarm, setSession, setActiveFarm }) {
  React.useEffect(() => {
    if (session?.role !== "solicitante") return;
    if (activeFarm?.id) return;
    setSession(null);
    setActiveFarm(null);
    clearStoredSession();
    clearStoredActiveFarm();
  }, [session?.role, activeFarm?.id, setSession, setActiveFarm]);
}
